import type {
  StatsAvailableFilters,
  StatsBpiMonthlyActivityOverview,
  StatsBpiMonthlyActivityRow,
  StatsBpiMonthlyRow,
  StatsBpiObjective,
  StatsBpiObjectives,
  StatsBpiOverview,
  StatsBoardCenterRow,
  StatsBoardComparisonMetric,
  StatsBoardMetricBase,
  StatsBoardSummary,
  StatsBoardTrendPoint,
  StatsBoardTrends,
  StatsBreakdownRow,
  StatsBreakdowns,
  StatsComparisonMetric,
  StatsDashboardErrors,
  StatsDashboardTrends,
  StatsDataQualityMetric,
  StatsEventInsights,
  StatsFunnelStep,
  StatsParticipantBand,
  StatsPipeline,
  StatsProcessCaseRow,
  StatsProcessDurationMetric,
  StatsProcessOverview,
  StatsProcessTrendPoint,
  StatsRangeKey,
  StatsReminderBreakdown,
  StatsSeasonalityCell,
  StatsServiceLevelMetric,
  StatsStatusDistributionRow,
  StatsSummary,
  StatsTrendGranularity,
  StatsTrendPoint,
  StatsUpcomingConfirmedWindow,
} from "./statsDashboardTypes";

export interface StatsRangeOption {
  key: StatsRangeKey;
  label: string;
}

export const STATS_RANGE_OPTIONS: StatsRangeOption[] = [
  { key: "90d", label: "90 jours" },
  { key: "all", label: "Tout depuis 01/11/25" },
];

export const STATS_DATA_START_DATE = new Date("2025-11-01T00:00:00.000Z");

export const PIPELINE_STATUS_ORDER = [
  "Non traité",
  "Standby",
  "Devis envoyé",
  "À relancer",
  "Déjà relancé",
  "Signé",
  "Acompte reçu",
  "Versement complet",
];

const EMPTY_DATA_QUALITY_METRIC: StatsDataQualityMetric = {
  filled: 0,
  total: 0,
  ratio: null,
};

const EMPTY_SERVICE_LEVEL_METRIC: StatsServiceLevelMetric = {
  sampleSize: 0,
  medianHours: null,
  p90Hours: null,
};

const EMPTY_PROCESS_DURATION_METRIC: StatsProcessDurationMetric = {
  sampleSize: 0,
  meanHours: null,
  medianHours: null,
  p90Hours: null,
};

const EMPTY_REMINDER_BREAKDOWN: StatsReminderBreakdown = {
  quoteRemindersSent: 0,
  casesWithClientReplyAfterQuoteReminder: 0,
  casesSignedAfterQuoteReminder: 0,
  depositRemindersSent: 0,
  casesMovedToDepositReceivedAfterReminder: 0,
};

const EMPTY_COMPARISON_METRIC: StatsComparisonMetric = {
  current: null,
  previous: null,
  deltaAbsolute: null,
  deltaRatio: null,
};

const EMPTY_BOARD_COMPARISON_METRIC: StatsBoardComparisonMetric = {
  current: null,
  previous: null,
  deltaAbsolute: null,
  deltaRatio: null,
};

export const EMPTY_STATS_ERRORS: StatsDashboardErrors = {
  filters: null,
  boardSummary: null,
  boardTrends: null,
  boardCenters: null,
  bpiOverview: null,
  bpiMonthlyActivity: null,
  eventInsights: null,
  process: null,
  funnel: null,
  pipeline: null,
};

function startOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0));
}

function addMonths(date: Date, months: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1, 0, 0, 0, 0));
}

export function buildStatsMonthRangeKey(date: Date): StatsRangeKey {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `month:${year}-${month}`;
}

export function getCurrentStatsMonthRangeKey(now = new Date()): StatsRangeKey {
  return buildStatsMonthRangeKey(startOfMonth(now));
}

export function isStatsMonthRangeKey(rangeKey: StatsRangeKey): rangeKey is `month:${string}` {
  return rangeKey.startsWith("month:");
}

export function parseStatsMonthRangeKey(
  rangeKey: StatsRangeKey,
): { year: number; month: number } | null {
  if (!isStatsMonthRangeKey(rangeKey)) return null;

  const match = rangeKey.match(/^month:(\d{4})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);

  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }

  return { year, month };
}

export function formatStatsMonthLabel(rangeKey: StatsRangeKey): string | null {
  const parsed = parseStatsMonthRangeKey(rangeKey);
  if (!parsed) return null;

  return new Intl.DateTimeFormat("fr-FR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(parsed.year, parsed.month - 1, 1, 0, 0, 0, 0)));
}

export function getStatsMonthOptions(now = new Date()): StatsRangeOption[] {
  const firstMonth = startOfMonth(STATS_DATA_START_DATE);
  const currentMonth = startOfMonth(now);
  const options: StatsRangeOption[] = [];

  for (
    let cursor = currentMonth;
    cursor.getTime() >= firstMonth.getTime();
    cursor = addMonths(cursor, -1)
  ) {
    const key = buildStatsMonthRangeKey(cursor);
    options.push({
      key,
      label: new Intl.DateTimeFormat("fr-FR", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(cursor),
    });
  }

  return options;
}

export function getStatsRangeDescription(rangeKey: StatsRangeKey): string {
  if (rangeKey === "90d") return "les 90 derniers jours";
  if (rangeKey === "all") return "tout l'historique CRM depuis le 1er novembre 2025";

  const monthLabel = formatStatsMonthLabel(rangeKey);
  return monthLabel ? `le mois de ${monthLabel}` : "la période sélectionnée";
}

export function getStatsDateRange(
  rangeKey: StatsRangeKey,
  now = new Date(),
): { from: Date; to: Date } {
  const parsedMonth = parseStatsMonthRangeKey(rangeKey);

  if (parsedMonth) {
    const from = new Date(Date.UTC(parsedMonth.year, parsedMonth.month - 1, 1, 0, 0, 0, 0));
    const to = new Date(Date.UTC(parsedMonth.year, parsedMonth.month, 1, 0, 0, 0, 0));

    return {
      from:
        from.getTime() < STATS_DATA_START_DATE.getTime() ? new Date(STATS_DATA_START_DATE) : from,
      to,
    };
  }

  const to = new Date(now);
  const from = new Date(now);

  switch (rangeKey) {
    case "90d":
      from.setUTCDate(from.getUTCDate() - 90);
      break;
    case "all":
    default:
      from.setTime(STATS_DATA_START_DATE.getTime());
      break;
  }

  if (from.getTime() < STATS_DATA_START_DATE.getTime()) {
    from.setTime(STATS_DATA_START_DATE.getTime());
  }

  return { from, to };
}

function toObject(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, "fr"));
}

function toNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function toNullableNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toNullableString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function toBoolean(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function normalizeDataQualityMetric(value: unknown): StatsDataQualityMetric {
  const raw = toObject(value);

  return {
    filled: toNumber(raw.filled, 0),
    total: toNumber(raw.total, 0),
    ratio: toNullableNumber(raw.ratio),
  };
}

function normalizeServiceLevelMetric(value: unknown): StatsServiceLevelMetric {
  const raw = toObject(value);

  return {
    sampleSize: toNumber(raw.sample_size, 0),
    medianHours: toNullableNumber(raw.median_hours),
    p90Hours: toNullableNumber(raw.p90_hours),
  };
}

function normalizeProcessDurationMetric(value: unknown): StatsProcessDurationMetric {
  const raw = toObject(value);

  return {
    sampleSize: toNumber(raw.sample_size, 0),
    meanHours: toNullableNumber(raw.mean_hours),
    medianHours: toNullableNumber(raw.median_hours),
    p90Hours: toNullableNumber(raw.p90_hours),
  };
}

function normalizeComparisonMetric(value: unknown): StatsComparisonMetric {
  const raw = toObject(value);

  return {
    current: toNullableNumber(raw.current),
    previous: toNullableNumber(raw.previous),
    deltaAbsolute: toNullableNumber(raw.delta_absolute),
    deltaRatio: toNullableNumber(raw.delta_ratio),
  };
}

function normalizeBoardComparisonMetric(value: unknown): StatsBoardComparisonMetric {
  const raw = toObject(value);

  return {
    current: toNullableNumber(raw.current),
    previous: toNullableNumber(raw.previous),
    deltaAbsolute: toNullableNumber(raw.delta_absolute),
    deltaRatio: toNullableNumber(raw.delta_ratio),
  };
}

function normalizeBoardMetricBase(value: unknown): StatsBoardMetricBase {
  const raw = toObject(value);

  return {
    sampleSize: toNullableNumber(raw.sample_size) ?? undefined,
    numerator: toNullableNumber(raw.numerator) ?? undefined,
    denominator: toNullableNumber(raw.denominator) ?? undefined,
    populatedCount: toNullableNumber(raw.populated_count) ?? undefined,
    totalCount: toNullableNumber(raw.total_count) ?? undefined,
  };
}

function formatTrendBucketLabel(
  bucketStart: string,
  bucketEnd: string,
  granularity: StatsTrendGranularity,
): string {
  const start = new Date(bucketStart);
  const end = new Date(bucketEnd);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return bucketStart;
  }

  if (granularity === "week") {
    const inclusiveEnd = new Date(end.getTime() - 1000);
    return `${start.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
    })} - ${inclusiveEnd.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
    })}`;
  }

  return start.toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
}

function normalizeTrendTimeline(
  value: unknown,
  granularity: StatsTrendGranularity,
): StatsTrendPoint[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((row) => {
      const raw = toObject(row);
      const bucketStart =
        typeof raw.bucket_start === "string" ? raw.bucket_start : new Date(0).toISOString();
      const bucketEnd =
        typeof raw.bucket_end === "string" ? raw.bucket_end : new Date(0).toISOString();

      return {
        bucketStart,
        bucketEnd,
        bucketLabel:
          typeof raw.bucket_label === "string"
            ? raw.bucket_label
            : formatTrendBucketLabel(bucketStart, bucketEnd, granularity),
        leadsCreated: toNumber(raw.leads_created, 0),
        quotesSent: toNumber(raw.quotes_sent, 0),
        signaturesReceived: toNumber(raw.signatures_received, 0),
        quoteRate: toNullableNumber(raw.quote_rate),
        signatureRate: toNullableNumber(raw.signature_rate),
        medianStaffReplyHours: toNullableNumber(raw.median_staff_reply_hours),
        medianLeadToFirstHumanHours: toNullableNumber(raw.median_lead_to_first_human_hours),
      };
    })
    .sort((a, b) => a.bucketStart.localeCompare(b.bucketStart));
}

function normalizeBoardTrendTimeline(
  value: unknown,
  granularity: StatsTrendGranularity,
): StatsBoardTrendPoint[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((row) => {
      const raw = toObject(row);
      const bucketStart =
        typeof raw.bucket_start === "string" ? raw.bucket_start : new Date(0).toISOString();
      const bucketEnd =
        typeof raw.bucket_end === "string" ? raw.bucket_end : new Date(0).toISOString();

      return {
        bucketStart,
        bucketEnd,
        bucketLabel:
          typeof raw.bucket_label === "string"
            ? raw.bucket_label
            : formatTrendBucketLabel(bucketStart, bucketEnd, granularity),
        incomingRequests: toNumber(raw.incoming_requests, 0),
        signedRequests: toNumber(raw.signed_requests, 0),
        transformationRate: toNullableNumber(raw.transformation_rate),
        signedBudgetTotal: toNullableNumber(raw.signed_budget_total),
      };
    })
    .sort((a, b) => a.bucketStart.localeCompare(b.bucketStart));
}

function normalizeBreakdownRows(value: unknown): StatsBreakdownRow[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((row) => {
      const raw = toObject(row);
      return {
        label: typeof raw.label === "string" ? raw.label : "Non renseigné",
        leads: toNumber(raw.leads, 0),
        quoteRate: toNullableNumber(raw.quote_rate),
        signatureRate: toNullableNumber(raw.signature_rate),
        upcomingConfirmedEvents: toNullableNumber(raw.upcoming_confirmed_events) ?? undefined,
        upcomingConfirmedParticipants:
          toNullableNumber(raw.upcoming_confirmed_participants) ?? undefined,
      };
    })
    .sort((a, b) => {
      if (b.leads !== a.leads) return b.leads - a.leads;
      return a.label.localeCompare(b.label, "fr");
    });
}

function normalizeBoardCenterRows(value: unknown): StatsBoardCenterRow[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((row) => {
      const raw = toObject(row);
      return {
        label: typeof raw.label === "string" ? raw.label : "Non renseigné",
        incomingRequests: toNumber(raw.incoming_requests, 0),
        signedRequests: toNumber(raw.signed_requests, 0),
        transformationRate: toNullableNumber(raw.transformation_rate),
        signedBudgetTotal: toNullableNumber(raw.signed_budget_total),
        signedBudgetMean: toNullableNumber(raw.signed_budget_mean),
        signedBudgetMedian: toNullableNumber(raw.signed_budget_median),
      };
    })
    .sort((a, b) => {
      if (b.incomingRequests !== a.incomingRequests) {
        return b.incomingRequests - a.incomingRequests;
      }
      return a.label.localeCompare(b.label, "fr");
    });
}

function normalizeBpiObjective(value: unknown): StatsBpiObjective {
  const raw = toObject(value);

  return {
    baselineValue: toNullableNumber(raw.baseline_value),
    targetValue: toNullableNumber(raw.target_value),
    entitlementValue: toNullableNumber(raw.entitlement_value),
    notes: toNullableString(raw.notes),
  };
}

function normalizeBpiObjectives(value: unknown): StatsBpiObjectives {
  const raw = toObject(value);

  return Object.fromEntries(
    Object.entries(raw).map(([metric, objective]) => [metric, normalizeBpiObjective(objective)]),
  );
}

function normalizeBpiRows(value: unknown): StatsBpiMonthlyRow[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((row) => {
      const raw = toObject(row);
      const rowKind: StatsBpiMonthlyRow["rowKind"] =
        raw.row_kind === "center" ? "center" : "total";

      return {
        rowKind,
        monthStart: typeof raw.month_start === "string" ? raw.month_start : "",
        monthEnd: typeof raw.month_end === "string" ? raw.month_end : "",
        monthLabel: typeof raw.month_label === "string" ? raw.month_label : "",
        centerLabel:
          typeof raw.center_label === "string" && raw.center_label.trim().length > 0
            ? raw.center_label
            : rowKind === "total"
              ? "Tous centres"
              : "Non renseigné",
        b2bEventsCount: toNumber(raw.b2b_events_count, 0),
        b2bEventsWithAmountCount: toNumber(raw.b2b_events_with_amount_count, 0),
        b2bRevenueHt: toNullableNumber(raw.b2b_revenue_ht),
        totalRevenueHt: toNullableNumber(raw.total_revenue_ht),
        b2bRevenueShare: toNullableNumber(raw.b2b_revenue_share),
        quotedCases: toNumber(raw.quoted_cases, 0),
        quotedCasesAbove800: toNumber(raw.quoted_cases_above_800, 0),
        quotedCasesBelow800: toNumber(raw.quoted_cases_below_800, 0),
        convertedQuotedCases: toNumber(raw.converted_quoted_cases, 0),
        quoteConversionRate: toNullableNumber(raw.quote_conversion_rate),
        averageTicketHt: toNullableNumber(raw.average_ticket_ht),
        medianTicketHt: toNullableNumber(raw.median_ticket_ht),
        smallTicketEventsCount: toNumber(raw.small_ticket_events_count, 0),
        lowPeriod: toBoolean(raw.low_period, false),
        lowPeriodReason: toNullableString(raw.low_period_reason),
        lowPeriodMedianEventsCount: toNullableNumber(raw.low_period_median_events_count),
        objectives: normalizeBpiObjectives(raw.objectives),
      };
    })
    .sort((left, right) => {
      const byMonth = right.monthStart.localeCompare(left.monthStart);
      if (byMonth !== 0) return byMonth;
      if (left.rowKind !== right.rowKind) return left.rowKind === "total" ? -1 : 1;
      return left.centerLabel.localeCompare(right.centerLabel, "fr");
    });
}

function normalizeBpiMonthlyActivityRows(value: unknown): StatsBpiMonthlyActivityRow[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((row) => {
      const raw = toObject(row);
      const rowKind: StatsBpiMonthlyActivityRow["rowKind"] =
        raw.row_kind === "center" ? "center" : "total";

      return {
        rowKind,
        monthStart: typeof raw.month_start === "string" ? raw.month_start : "",
        monthEnd: typeof raw.month_end === "string" ? raw.month_end : "",
        monthLabel: typeof raw.month_label === "string" ? raw.month_label : "",
        centerLabel:
          typeof raw.center_label === "string" && raw.center_label.trim().length > 0
            ? raw.center_label
            : rowKind === "total"
              ? "Tous centres"
              : "Non renseigné",
        confirmedEventsCount: toNumber(raw.confirmed_events_count, 0),
        associationRequests: toNumber(raw.association_requests, 0),
        b2bRequests: toNumber(raw.b2b_requests, 0),
        unspecifiedRequests: toNumber(raw.unspecified_requests, 0),
        otherRequests: toNumber(raw.other_requests, 0),
        totalRequests: toNumber(raw.total_requests, 0),
        totalSales: toNumber(raw.total_sales, 0),
      };
    })
    .sort((left, right) => {
      const byMonth = right.monthStart.localeCompare(left.monthStart);
      if (byMonth !== 0) return byMonth;
      if (left.rowKind !== right.rowKind) return left.rowKind === "total" ? -1 : 1;
      return left.centerLabel.localeCompare(right.centerLabel, "fr");
    });
}

function normalizeReminderBreakdown(value: unknown): StatsReminderBreakdown {
  const raw = toObject(value);

  return {
    quoteRemindersSent: toNumber(raw.quote_reminders_sent, 0),
    casesWithClientReplyAfterQuoteReminder: toNumber(
      raw.cases_with_client_reply_after_quote_reminder,
      0,
    ),
    casesSignedAfterQuoteReminder: toNumber(raw.cases_signed_after_quote_reminder, 0),
    depositRemindersSent: toNumber(raw.deposit_reminders_sent, 0),
    casesMovedToDepositReceivedAfterReminder: toNumber(
      raw.cases_moved_to_deposit_received_after_reminder,
      0,
    ),
  };
}

function normalizeParticipantBands(value: unknown): StatsParticipantBand[] {
  if (!Array.isArray(value)) return [];

  return value.map((row) => {
    const raw = toObject(row);
    return {
      label: typeof raw.label === "string" ? raw.label : "Non renseigné",
      count: toNumber(raw.count, 0),
    };
  });
}

function normalizeSeasonality(value: unknown): StatsSeasonalityCell[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((row) => {
      const raw = toObject(row);
      return {
        dayIndex: toNumber(raw.day_index, 0),
        dayLabel: typeof raw.day_label === "string" ? raw.day_label : "Jour",
        hourStart: toNumber(raw.hour_start, 0),
        hourEnd: toNumber(raw.hour_end, 0),
        slotLabel: typeof raw.slot_label === "string" ? raw.slot_label : "Créneau",
        eventsCount: toNumber(raw.events_count, 0),
      };
    })
    .sort((a, b) => {
      if (a.dayIndex !== b.dayIndex) return a.dayIndex - b.dayIndex;
      return a.hourStart - b.hourStart;
    });
}

function normalizeProcessTimeline(
  value: unknown,
  granularity: StatsTrendGranularity,
): StatsProcessTrendPoint[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((row) => {
      const raw = toObject(row);
      const bucketStart =
        typeof raw.bucket_start === "string" ? raw.bucket_start : new Date(0).toISOString();
      const bucketEnd =
        typeof raw.bucket_end === "string" ? raw.bucket_end : new Date(0).toISOString();

      return {
        bucketStart,
        bucketEnd,
        bucketLabel:
          typeof raw.bucket_label === "string"
            ? raw.bucket_label
            : formatTrendBucketLabel(bucketStart, bucketEnd, granularity),
        meanFirstHumanReplyHours: toNullableNumber(raw.mean_first_human_reply_hours),
        meanLeadToSignedHours: toNullableNumber(raw.mean_lead_to_signed_hours),
        medianFirstHumanReplyHours: toNullableNumber(raw.median_first_human_reply_hours),
        medianLeadToSignedHours: toNullableNumber(raw.median_lead_to_signed_hours),
      };
    })
    .sort((a, b) => a.bucketStart.localeCompare(b.bucketStart));
}

function normalizeProcessCaseRows(value: unknown): StatsProcessCaseRow[] {
  if (!Array.isArray(value)) return [];

  return value.map((row) => {
    const raw = toObject(row);

    return {
      caseId: typeof raw.case_id === "string" ? raw.case_id : "",
      companyName: toNullableString(raw.company_name),
      title: toNullableString(raw.title),
      center:
        typeof raw.center === "string" && raw.center.trim().length > 0
          ? raw.center
          : "Non renseigné",
      createdAt: typeof raw.created_at === "string" ? raw.created_at : new Date(0).toISOString(),
      status: typeof raw.status === "string" ? raw.status : "Inconnu",
      firstHumanReplyHours: toNullableNumber(raw.first_human_reply_hours),
      leadToSignedHours: toNullableNumber(raw.lead_to_signed_hours),
    };
  });
}

export function normalizeStatsFilters(value: unknown): StatsAvailableFilters {
  const raw = toObject(value);
  return {
    centers: toStringArray(raw.centers),
    clientTypes: toStringArray(raw.client_types),
  };
}

export function normalizeStatsSummary(value: unknown): StatsSummary {
  const raw = toObject(value);
  const dataQuality = toObject(raw.data_quality);
  const serviceLevels = toObject(raw.service_levels);

  return {
    leadsCreated: toNumber(raw.leads_created, 0),
    quotesSent: toNumber(raw.quotes_sent, 0),
    signaturesReceived: toNumber(raw.signatures_received, 0),
    medianStaffReplyHours: toNullableNumber(raw.median_staff_reply_hours),
    upcomingConfirmedEvents: toNumber(raw.upcoming_confirmed_events, 0),
    upcomingConfirmedParticipants: toNumber(raw.upcoming_confirmed_participants, 0),
    dataQuality: {
      invoiceAmount: normalizeDataQualityMetric(dataQuality.invoice_amount),
      participants: normalizeDataQualityMetric(dataQuality.participants),
      eventDate: normalizeDataQualityMetric(dataQuality.event_date),
      phone: normalizeDataQualityMetric(dataQuality.phone),
    },
    serviceLevels: {
      leadToFirstHumanStaffMessage: normalizeServiceLevelMetric(
        serviceLevels.lead_to_first_human_staff_message,
      ),
      leadToFirstQuoteSent: normalizeServiceLevelMetric(serviceLevels.lead_to_first_quote_sent),
      clientMessageToHumanStaffReply: normalizeServiceLevelMetric(
        serviceLevels.client_message_to_human_staff_reply,
      ),
      lastQuoteSentToSignature: normalizeServiceLevelMetric(
        serviceLevels.last_quote_sent_to_signature,
      ),
    },
  };
}

export function normalizeStatsFunnel(value: unknown): StatsFunnelStep[] {
  if (!Array.isArray(value)) return [];

  const rows = value.map((step) => {
    const raw = toObject(step);
    return {
      step: typeof raw.step === "string" ? raw.step : "Étape",
      count: toNumber(raw.count, 0),
      previousCount: toNumber(raw.previous_count, 0),
      deltaAbsolute: toNumber(raw.delta_absolute, 0),
      deltaRatio: toNullableNumber(raw.delta_ratio),
      conversionToNextRate: toNullableNumber(raw.conversion_to_next_rate),
      rateVsPrevious: toNullableNumber(raw.rate_vs_previous),
      rateVsInitial: toNullableNumber(raw.rate_vs_initial),
    };
  });

  return rows.map((row, index) => {
    if (row.conversionToNextRate !== null) return row;

    const nextStep = rows[index + 1];
    if (!nextStep || row.count === 0) {
      return {
        ...row,
        conversionToNextRate: null,
      };
    }

    return {
      ...row,
      conversionToNextRate: nextStep.count / row.count,
    };
  });
}

export function normalizeStatsTrends(value: unknown): StatsDashboardTrends {
  const raw = toObject(value);
  const rawGranularity = raw.granularity;
  const granularity: StatsTrendGranularity =
    rawGranularity === "week" || rawGranularity === "month" ? rawGranularity : "month";
  const comparisons = toObject(raw.comparisons);

  return {
    granularity,
    timeline: normalizeTrendTimeline(raw.timeline, granularity),
    comparisons: {
      leadsCreated: normalizeComparisonMetric(comparisons.leads_created),
      quotesSent: normalizeComparisonMetric(comparisons.quotes_sent),
      signaturesReceived: normalizeComparisonMetric(comparisons.signatures_received),
      signatureRate: normalizeComparisonMetric(comparisons.signature_rate),
      medianStaffReplyHours: normalizeComparisonMetric(comparisons.median_staff_reply_hours),
    },
  };
}

function normalizeStatusDistribution(value: unknown): StatsStatusDistributionRow[] {
  if (!Array.isArray(value)) return [];

  const rows = value.map((row) => {
    const raw = toObject(row);
    return {
      status: typeof raw.status === "string" ? raw.status : "Inconnu",
      count: toNumber(raw.count, 0),
      medianAgeHours: toNullableNumber(raw.median_age_hours),
    };
  });

  return rows.sort((a, b) => {
    const aIndex = PIPELINE_STATUS_ORDER.indexOf(a.status);
    const bIndex = PIPELINE_STATUS_ORDER.indexOf(b.status);
    const safeA = aIndex === -1 ? PIPELINE_STATUS_ORDER.length : aIndex;
    const safeB = bIndex === -1 ? PIPELINE_STATUS_ORDER.length : bIndex;
    return safeA - safeB;
  });
}

function normalizeUpcomingConfirmed(value: unknown): StatsUpcomingConfirmedWindow[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((row) => {
      const raw = toObject(row);
      return {
        days: toNumber(raw.days, 0),
        eventsCount: toNumber(raw.events_count, 0),
        participantsCount: toNumber(raw.participants_count, 0),
      };
    })
    .sort((a, b) => a.days - b.days);
}

export function normalizeStatsPipeline(value: unknown): StatsPipeline {
  const raw = toObject(value);
  const urgentBacklog = toObject(raw.urgent_backlog);

  return {
    statusDistribution: normalizeStatusDistribution(raw.status_distribution),
    urgentBacklog: {
      nonTraiteOver24hWithoutHumanStaff: toNumber(
        urgentBacklog.non_traite_over_24h_without_human_staff,
        0,
      ),
      quoteWaitingOver72hWithoutClientReply: toNumber(
        urgentBacklog.quote_waiting_over_72h_without_client_reply,
        0,
      ),
      signedEventWithin7dWithoutDeposit: toNumber(
        urgentBacklog.signed_event_within_7d_without_deposit,
        0,
      ),
    },
    upcomingConfirmed: normalizeUpcomingConfirmed(raw.upcoming_confirmed),
  };
}

export function normalizeStatsBreakdowns(value: unknown): StatsBreakdowns {
  const raw = toObject(value);

  return {
    byCenter: normalizeBreakdownRows(raw.by_center),
    bySource: normalizeBreakdownRows(raw.by_source),
    byClientType: normalizeBreakdownRows(raw.by_client_type),
    reminders: normalizeReminderBreakdown(raw.reminders),
  };
}

export function normalizeBoardSummary(value: unknown): StatsBoardSummary {
  const raw = toObject(value);
  const comparisons = toObject(raw.comparisons);
  const bases = toObject(raw.bases);

  return {
    incomingRequests: toNumber(raw.incoming_requests, 0),
    signedRequests: toNumber(raw.signed_requests, 0),
    transformationRate: toNullableNumber(raw.transformation_rate),
    signedBudgetTotal: toNullableNumber(raw.signed_budget_total),
    signedBudgetMean: toNullableNumber(raw.signed_budget_mean),
    signedBudgetMedian: toNullableNumber(raw.signed_budget_median),
    comparisons: {
      incomingRequests: normalizeBoardComparisonMetric(comparisons.incoming_requests),
      signedRequests: normalizeBoardComparisonMetric(comparisons.signed_requests),
      transformationRate: normalizeBoardComparisonMetric(comparisons.transformation_rate),
      signedBudgetTotal: normalizeBoardComparisonMetric(comparisons.signed_budget_total),
      signedBudgetMean: normalizeBoardComparisonMetric(comparisons.signed_budget_mean),
      signedBudgetMedian: normalizeBoardComparisonMetric(comparisons.signed_budget_median),
    },
    bases: {
      incomingRequests: normalizeBoardMetricBase(bases.incoming_requests),
      signedRequests: normalizeBoardMetricBase(bases.signed_requests),
      transformationRate: normalizeBoardMetricBase(bases.transformation_rate),
      signedBudgetTotal: normalizeBoardMetricBase(bases.signed_budget_total),
      signedBudgetMean: normalizeBoardMetricBase(bases.signed_budget_mean),
      signedBudgetMedian: normalizeBoardMetricBase(bases.signed_budget_median),
    },
  };
}

export function normalizeBoardTrends(value: unknown): StatsBoardTrends {
  const raw = toObject(value);
  const rawGranularity = raw.granularity;
  const granularity: StatsTrendGranularity =
    rawGranularity === "week" || rawGranularity === "month" ? rawGranularity : "month";

  return {
    granularity,
    timeline: normalizeBoardTrendTimeline(raw.timeline, granularity),
  };
}

export function normalizeBoardCenters(value: unknown): StatsBoardCenterRow[] {
  return normalizeBoardCenterRows(value);
}

export function normalizeBpiOverview(value: unknown): StatsBpiOverview {
  const raw = toObject(value);

  return {
    clientScope: toStringArray(raw.client_scope),
    monthFrom: toNullableString(raw.month_from),
    monthTo: toNullableString(raw.month_to),
    rows: normalizeBpiRows(raw.rows),
  };
}

export function normalizeBpiMonthlyActivityOverview(
  value: unknown,
): StatsBpiMonthlyActivityOverview {
  const raw = toObject(value);

  return {
    monthFrom: toNullableString(raw.month_from),
    monthTo: toNullableString(raw.month_to),
    rows: normalizeBpiMonthlyActivityRows(raw.rows),
  };
}

export function normalizeEventInsights(value: unknown): StatsEventInsights {
  const raw = toObject(value);
  const seasonality = normalizeSeasonality(raw.seasonality);
  const maxSeasonalityCount = seasonality.reduce((max, row) => Math.max(max, row.eventsCount), 0);
  const confirmedEventsCount = toNumber(raw.confirmed_events_count, 0);
  const averageParticipantsSampleSize = toNumber(raw.average_participants_sample_size, 0);
  const participantsKnownEventsCount =
    toNullableNumber(raw.participants_known_events_count) ?? averageParticipantsSampleSize;
  const participantsCoverageRate =
    toNullableNumber(raw.participants_coverage_rate) ??
    (confirmedEventsCount > 0 ? participantsKnownEventsCount / confirmedEventsCount : null);

  return {
    confirmedEventsCount,
    averageParticipantsPerEvent: toNullableNumber(raw.average_participants_per_event),
    averageParticipantsSampleSize,
    participantsKnownEventsCount,
    participantsCoverageRate,
    missingParticipantsCount: toNumber(raw.missing_participants_count, 0),
    participantBands: normalizeParticipantBands(raw.participant_bands),
    seasonality,
    maxSeasonalityCount,
  };
}

export function normalizeProcessOverview(value: unknown): StatsProcessOverview {
  const raw = toObject(value);
  const rawGranularity = raw.granularity;
  const granularity: StatsTrendGranularity =
    rawGranularity === "week" || rawGranularity === "month" ? rawGranularity : "month";

  return {
    granularity,
    firstHumanReply: normalizeProcessDurationMetric(raw.first_human_reply),
    leadToSigned: normalizeProcessDurationMetric(raw.lead_to_signed),
    timeline: normalizeProcessTimeline(raw.timeline, granularity),
    caseRows: normalizeProcessCaseRows(raw.case_rows),
  };
}

export function formatInteger(value: number): string {
  return value.toLocaleString("fr-FR");
}

export function formatPercent(value: number | null): string {
  if (value === null) return "N/A";
  return `${(value * 100).toFixed(1)} %`;
}

export function formatCurrency(value: number | null): string {
  if (value === null) return "N/A";
  return value.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
}

export function formatDecimal(value: number | null, digits = 1): string {
  if (value === null) return "N/A";
  return value.toLocaleString("fr-FR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function formatDurationHours(value: number | null): string {
  if (value === null) return "N/A";
  if (value < 1) {
    return `${Math.max(1, Math.round(value * 60))} min`;
  }

  const totalHours = Math.round(value * 10) / 10;
  if (totalHours < 24) {
    return `${totalHours.toLocaleString("fr-FR", {
      minimumFractionDigits: totalHours % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 1,
    })} h`;
  }

  const days = Math.floor(totalHours / 24);
  const remainingHours = Math.round((totalHours - days * 24) * 10) / 10;

  if (remainingHours === 0) {
    return `${days} j`;
  }

  return `${days} j ${remainingHours.toLocaleString("fr-FR", {
    minimumFractionDigits: remainingHours % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  })} h`;
}

export function formatRateWithBase(metric: StatsDataQualityMetric): string {
  if (metric.total === 0 || metric.ratio === null) return "Aucune donnée";
  return `${formatPercent(metric.ratio)} (${formatInteger(metric.filled)}/${formatInteger(metric.total)})`;
}

export function buildSectionErrorMessage(section: string, error: unknown): string {
  if (error instanceof Error && error.message) {
    return `${section}: ${error.message}`;
  }
  return `${section}: erreur de chargement`;
}

export function createEmptySummary(): StatsSummary {
  return {
    leadsCreated: 0,
    quotesSent: 0,
    signaturesReceived: 0,
    medianStaffReplyHours: null,
    upcomingConfirmedEvents: 0,
    upcomingConfirmedParticipants: 0,
    dataQuality: {
      invoiceAmount: { ...EMPTY_DATA_QUALITY_METRIC },
      participants: { ...EMPTY_DATA_QUALITY_METRIC },
      eventDate: { ...EMPTY_DATA_QUALITY_METRIC },
      phone: { ...EMPTY_DATA_QUALITY_METRIC },
    },
    serviceLevels: {
      leadToFirstHumanStaffMessage: { ...EMPTY_SERVICE_LEVEL_METRIC },
      leadToFirstQuoteSent: { ...EMPTY_SERVICE_LEVEL_METRIC },
      clientMessageToHumanStaffReply: { ...EMPTY_SERVICE_LEVEL_METRIC },
      lastQuoteSentToSignature: { ...EMPTY_SERVICE_LEVEL_METRIC },
    },
  };
}

export function createEmptyPipeline(): StatsPipeline {
  return {
    statusDistribution: [],
    urgentBacklog: {
      nonTraiteOver24hWithoutHumanStaff: 0,
      quoteWaitingOver72hWithoutClientReply: 0,
      signedEventWithin7dWithoutDeposit: 0,
    },
    upcomingConfirmed: [],
  };
}

export function createEmptyBreakdowns(): StatsBreakdowns {
  return {
    byCenter: [],
    bySource: [],
    byClientType: [],
    reminders: { ...EMPTY_REMINDER_BREAKDOWN },
  };
}

export function createEmptyTrends(): StatsDashboardTrends {
  return {
    granularity: "month",
    timeline: [],
    comparisons: {
      leadsCreated: { ...EMPTY_COMPARISON_METRIC },
      quotesSent: { ...EMPTY_COMPARISON_METRIC },
      signaturesReceived: { ...EMPTY_COMPARISON_METRIC },
      signatureRate: { ...EMPTY_COMPARISON_METRIC },
      medianStaffReplyHours: { ...EMPTY_COMPARISON_METRIC },
    },
  };
}

export function createEmptyBoardSummary(): StatsBoardSummary {
  return {
    incomingRequests: 0,
    signedRequests: 0,
    transformationRate: null,
    signedBudgetTotal: null,
    signedBudgetMean: null,
    signedBudgetMedian: null,
    comparisons: {
      incomingRequests: { ...EMPTY_BOARD_COMPARISON_METRIC },
      signedRequests: { ...EMPTY_BOARD_COMPARISON_METRIC },
      transformationRate: { ...EMPTY_BOARD_COMPARISON_METRIC },
      signedBudgetTotal: { ...EMPTY_BOARD_COMPARISON_METRIC },
      signedBudgetMean: { ...EMPTY_BOARD_COMPARISON_METRIC },
      signedBudgetMedian: { ...EMPTY_BOARD_COMPARISON_METRIC },
    },
    bases: {
      incomingRequests: {},
      signedRequests: {},
      transformationRate: {},
      signedBudgetTotal: {},
      signedBudgetMean: {},
      signedBudgetMedian: {},
    },
  };
}

export function createEmptyBoardTrends(): StatsBoardTrends {
  return {
    granularity: "month",
    timeline: [],
  };
}

export function createEmptyBpiOverview(): StatsBpiOverview {
  return {
    clientScope: [],
    monthFrom: null,
    monthTo: null,
    rows: [],
  };
}

export function createEmptyBpiMonthlyActivityOverview(): StatsBpiMonthlyActivityOverview {
  return {
    monthFrom: null,
    monthTo: null,
    rows: [],
  };
}

export function createEmptyEventInsights(): StatsEventInsights {
  return {
    confirmedEventsCount: 0,
    averageParticipantsPerEvent: null,
    averageParticipantsSampleSize: 0,
    participantsKnownEventsCount: 0,
    participantsCoverageRate: null,
    missingParticipantsCount: 0,
    participantBands: [
      { label: "0-20", count: 0 },
      { label: "21-50", count: 0 },
      { label: "51-100", count: 0 },
      { label: "101-150", count: 0 },
      { label: "+150", count: 0 },
    ],
    seasonality: [],
    maxSeasonalityCount: 0,
  };
}

export function createEmptyProcessOverview(): StatsProcessOverview {
  return {
    granularity: "month",
    firstHumanReply: { ...EMPTY_PROCESS_DURATION_METRIC },
    leadToSigned: { ...EMPTY_PROCESS_DURATION_METRIC },
    timeline: [],
    caseRows: [],
  };
}
